import {Component, computed, inject, input, linkedSignal, OnInit, output} from "@angular/core";
import {Translations} from "@translations";
import {zoomInOnEnterAnimation} from "angular-animations";
import {ActionMenuComponent} from "@common/ui/action-menu/action-menu.component";
import {NotificationService, SimMaterialDialogService, SimSessionService} from "@services";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {MatIconModule} from "@angular/material/icon";
import {EnumLabel, EnumLabelPipe} from "../../../../../../shared/shared-pipes/enum-label.pipe";
import {DatePipe} from "@angular/common";
import {SessionStatusTagComponent} from "@common/components/session/session-status-tag/session-status-tag.component";
import {AutomaticRecordingSession, Event, SessionState} from "@models";
import {MatButton} from "@angular/material/button";
import {SessionRunPreviewComponent, SessionRunWrapper} from "@common/components/session/session-run-preview/session-run-preview.component";
import {SessionStationPreviewComponent} from "@common/components/session/session-station-preview/session-station-preview.component";
import {chunk} from "lodash-es";
import {EventEmitterService} from "@event-emitter";
import {AutomaticRecordingSessionCardStationContentComponent} from "@common/components/session/session-card/session-type/automatic-recording/content/automatic-recording-session-card-station-content.component";

@Component({
	selector: "sim-automatic-recording-session-card",
	animations: [zoomInOnEnterAnimation({duration: 175})],
	imports: [
		EnumLabelPipe,
		ActionMenuComponent,
		MatIconModule,
		DatePipe,
		SessionStatusTagComponent,
		TranslateModule,
		MatButton,
		SessionRunPreviewComponent,
		SessionStationPreviewComponent,
		AutomaticRecordingSessionCardStationContentComponent,
	],
	templateUrl: "./automatic-recording-session-card.component.html",
	styleUrl: "./automatic-recording-session-card.component.scss",
})
export class AutomaticRecordingSessionCardComponent implements OnInit {
	protected readonly T = Translations;
	protected readonly EnumLabel = EnumLabel;

	// I/O
	public readonly inputSession = input.required<AutomaticRecordingSession>({alias: "session"});
	public readonly event = input.required<Event>();
	public readonly showActionMenu = input<boolean>(false);
	public readonly onDelete = output<void>();
	public readonly onEdit = output<void>();

	readonly #dialogService = inject(SimMaterialDialogService);
	readonly #sessionService = inject(SimSessionService);
	readonly #notificationService = inject(NotificationService);
	readonly #eventEmitter = inject(EventEmitterService);
	readonly translate = inject(TranslateService);

	protected readonly session = linkedSignal<AutomaticRecordingSession>(() => this.inputSession());
	protected readonly runs = computed<SessionRunWrapper[]>(() => computeRuns(this.session()));
	protected readonly canCancelSession = computed<boolean>(
		() => ![SessionState.Scheduled, SessionState.Aborted, SessionState.Finished].includes(this.session().state),
	);

	ngOnInit(): void {
		//Subscribe to session updates
		this.#eventEmitter.onTrainingSessionUpdated.subscribe((updatedSessionEvent) => {
			if (updatedSessionEvent.simSession.id === this.session()?.id) {
				this.session.set(updatedSessionEvent.simSession as AutomaticRecordingSession);
			}
		});
	}

	protected confirmDelete(): void {
		this.#dialogService
			.openDeleteDialog({
				deleteTitle: this.translate.instant(this.T.SESSION_SELECTION_DELETE_CREATED_SESSION),
				deleteText: this.translate.instant(this.T.SESSION_SELECTION_DELETE_CREATED_SESSION_TXT),
				deleteButtonText: this.translate.instant(this.T.BUTTON_DELETE),
				cancelText: this.translate.instant(this.T.BUTTON_CANCEL),
			})
			.then(({action}) => {
				if (action === "ok") {
					this.onDelete.emit();
				}
			});
	}

	protected cancelSession(): void {
		this.#dialogService
			.openConfirmationDialog({
				title: this.T.CONFIRMATION_DIALOG_ABORT_SESSION_TITLE,
				cancelText: this.T.BUTTON_CANCEL,
				description: this.T.CONFIRMATION_DIALOG_ABORT_SESSION_DESC,
				okText: this.T.BUTTON_OK,
			})
			.then((dialogResult) => {
				if (dialogResult.action === "ok") {
					this.#sessionService.abortSession({sessionId: this.session().id}).then(() => {
						this.#notificationService.notifySuccess(
							this.translate.instant(Translations.CONFIRMATION_DIALOG_ABORT_SESSION_SUCCESS),
						);
					});
				}
			});
	}

	protected openEdit(): Function {
		return () => this.onEdit.emit();
	}
}

function computeRuns(session: AutomaticRecordingSession): SessionRunWrapper[] {
	const result: SessionRunWrapper[] = [];
	let startTime = new Date(session.startTime);

	chunk(session.schedule, 2).forEach(([run, intermission]) => {
		const durationMinutes = Math.floor(run.duration / 60_000);
		const intermissionDurationMinutes = intermission?.duration ? Math.floor(intermission.duration / 60_000) : undefined;

		result.push({
			runId: run.id,
			startTime,
			durationMinutes,
			intermissionDurationMinutes,
		});

		startTime = new Date(startTime.getTime() + run.duration + intermission?.duration);
	});

	return result;
}
